/**
 * Data transformation utilities for Sankey diagram visualization
 * Converts API data to @nivo/sankey format
 */

import {
  MappingVisualizationData,
  FilterOptions,
  DataFileWithMappings,
  ColumnMapping,
} from '../types/mapping';

export interface SankeyNode {
  id: string;
  nodeColor?: string;
}

export interface SankeyLink {
  source: string;
  target: string;
  value: number;
  startColor?: string;
  endColor?: string;
}

export interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

/**
 * Get color based on mapping status
 */
function getNodeColor(type: 'file' | 'column' | 'property' | 'schemaNode', status?: 'mapped' | 'unmapped' | 'accepted'): string {
  switch (type) {
    case 'file':
      return '#3b82f6'; // Blue
    case 'column':
      if (status === 'accepted') return '#10b981'; // Green
      if (status === 'mapped') return '#f59e0b'; // Amber
      return '#ef4444'; // Red (unmapped)
    case 'property':
      return '#86efac'; // Light green
    case 'schemaNode':
      return '#059669'; // Dark green
    default:
      return '#9ca3af'; // Gray
  }
}

/**
 * Get link color based on confidence
 */
function getLinkColor(confidence?: number): { start: string; end: string } {
  if (!confidence) return { start: '#9ca3af80', end: '#9ca3af80' };
  if (confidence >= 0.9) return { start: '#10b98180', end: '#10b98180' }; // Green with transparency
  if (confidence >= 0.7) return { start: '#f59e0b80', end: '#f59e0b80' }; // Amber with transparency
  return { start: '#ef444480', end: '#ef444480' }; // Red with transparency
}

/**
 * Get the actual mapping object from a column
 * Handles both direct suggestedMapping and nested acceptedMapping.suggestedMapping
 */
function getMapping(column: ColumnMapping) {
  if (column.acceptedMapping) {
    // acceptedMapping has suggestedMapping nested inside
    return column.acceptedMapping.suggestedMapping;
  }
  return column.suggestedMapping;
}

/**
 * Check if column matches filter criteria
 */
function matchesFilter(
  column: ColumnMapping,
  fileId: string,
  filter: FilterOptions
): boolean {
  // File filter
  if (filter.selectedFiles.length > 0 && !filter.selectedFiles.includes(fileId)) {
    return false;
  }

  const mapping = getMapping(column);
  const isMapped = !!mapping;

  // Confidence filter
  if (mapping && mapping.confidence < filter.minConfidence) {
    return false;
  }

  // Show unmapped filter
  if (!filter.showUnmapped && !isMapped) {
    return false;
  }

  // Show accepted only
  if (filter.showAcceptedOnly && !column.acceptedMapping) {
    return false;
  }

  // Show suggested only
  if (filter.showSuggestedOnly && !isMapped) {
    return false;
  }

  // Show only unmapped
  if (filter.showOnlyUnmapped && isMapped) {
    return false;
  }

  // Search term
  if (filter.searchTerm) {
    const term = filter.searchTerm.toLowerCase();
    const columnName = column.columnName.toLowerCase();
    const nodeLabel = mapping?.nodeLabel?.toLowerCase() || '';
    const propertyName = mapping?.propertyName?.toLowerCase() || '';

    if (!columnName.includes(term) && !nodeLabel.includes(term) && !propertyName.includes(term)) {
      return false;
    }
  }

  return true;
}

/**
 * Transform mapping data to Sankey format
 */
export function transformToSankey(
  data: MappingVisualizationData | null,
  filter: FilterOptions
): SankeyData {
  const nodes: SankeyNode[] = [];
  const links: SankeyLink[] = [];
  const nodeIds = new Set<string>();

  if (!data) {
    return { nodes, links };
  }

  const { dataFiles, schemaDesign } = data;

  // Track which schema nodes and properties are used
  const usedPropertyIds = new Set<string>();
  const usedNodeIds = new Set<string>();

  // STEP 1: Process files and columns
  console.log('[SankeyTransformer] Processing', dataFiles.length, 'files');
  dataFiles.forEach((file) => {
    const fileId = `file_${file.fileId}`;

    // Add file node
    if (!nodeIds.has(fileId)) {
      nodes.push({
        id: fileId,
        nodeColor: getNodeColor('file'),
      });
      nodeIds.add(fileId);
    }

    // Process columns
    file.columns.forEach((column) => {
      if (!matchesFilter(column, file.fileId, filter)) {
        return;
      }

      const columnId = `col_${file.fileId}_${column.columnName}`;
      const mapping = getMapping(column);
      if (mapping) {
        console.log('[SankeyTransformer] Column', column.columnName, '→', mapping.nodeLabel, '.', mapping.propertyName);
      }
      const isMapped = !!mapping;
      const isAccepted = !!column.acceptedMapping;

      // Determine column status
      const columnStatus = !isMapped ? 'unmapped' : (isAccepted ? 'accepted' : 'mapped');

      // Add column node
      if (!nodeIds.has(columnId)) {
        nodes.push({
          id: columnId,
          nodeColor: getNodeColor('column', columnStatus),
        });
        nodeIds.add(columnId);
      }

      // Link file -> column (value = 1 for structure)
      links.push({
        source: fileId,
        target: columnId,
        value: 1,
        startColor: getNodeColor('file'),
        endColor: getNodeColor('column', columnStatus),
      });

      // If mapped, create property and schema node links
      if (mapping) {
        const propertyId = `prop_${mapping.nodeLabel}_${mapping.propertyName}`;
        const schemaNodeId = `schema_${mapping.nodeLabel}`;

        // Track usage
        usedPropertyIds.add(propertyId);
        usedNodeIds.add(schemaNodeId);

        // Add property node
        if (!nodeIds.has(propertyId)) {
          nodes.push({
            id: propertyId,
            nodeColor: getNodeColor('property'),
          });
          nodeIds.add(propertyId);
        }

        // Link column -> property (value = confidence for visual weight)
        const colors = getLinkColor(mapping.confidence);
        links.push({
          source: columnId,
          target: propertyId,
          value: mapping.confidence,
          startColor: colors.start,
          endColor: colors.end,
        });
      }
    });
  });

  // STEP 2: Create schema nodes and link properties to them
  if (schemaDesign) {
    schemaDesign.nodes.forEach((node) => {
      const schemaNodeId = `schema_${node.label}`;

      // Only add schema nodes that have properties being used
      if (usedNodeIds.has(schemaNodeId)) {
        if (!nodeIds.has(schemaNodeId)) {
          nodes.push({
            id: schemaNodeId,
            nodeColor: getNodeColor('schemaNode'),
          });
          nodeIds.add(schemaNodeId);
        }

        // Link each used property to its schema node
        if (node.properties) {
          node.properties.forEach((prop) => {
            const propertyId = `prop_${node.label}_${prop.name}`;

            if (usedPropertyIds.has(propertyId)) {
              links.push({
                source: propertyId,
                target: schemaNodeId,
                value: 1,
                startColor: getNodeColor('property'),
                endColor: getNodeColor('schemaNode'),
              });
            }
          });
        }
      }
    });
  }

  console.log('[SankeyTransformer] Created', nodes.length, 'nodes and', links.length, 'links');
  console.log('[SankeyTransformer] Node breakdown:', {
    files: nodes.filter(n => n.id.startsWith('file_')).length,
    columns: nodes.filter(n => n.id.startsWith('col_')).length,
    properties: nodes.filter(n => n.id.startsWith('prop_')).length,
    schemaNodes: nodes.filter(n => n.id.startsWith('schema_')).length,
  });
  console.log('[SankeyTransformer] Schema nodes created:', nodes.filter(n => n.id.startsWith('schema_')).map(n => n.id));
  console.log('[SankeyTransformer] Used node IDs:', Array.from(usedNodeIds));
  console.log('[SankeyTransformer] Schema design available:', !!schemaDesign, schemaDesign?.nodes?.length || 0);

  return { nodes, links };
}

/**
 * Get readable label for node ID
 */
export function getNodeLabel(nodeId: string, data: MappingVisualizationData | null): string {
  if (!data) return nodeId;

  // File nodes
  if (nodeId.startsWith('file_')) {
    const fileId = nodeId.replace('file_', '');
    const file = data.dataFiles.find(f => f.fileId === fileId);
    return file?.fileName || nodeId;
  }

  // Column nodes
  if (nodeId.startsWith('col_')) {
    // Pattern: col_<fileId>_<columnName>
    // Remove 'col_' prefix first
    const withoutPrefix = nodeId.substring(4); // Remove 'col_'
    // Find the first underscore after file ID and take everything after it
    const firstUnderscoreIndex = withoutPrefix.indexOf('_');
    if (firstUnderscoreIndex !== -1) {
      return withoutPrefix.substring(firstUnderscoreIndex + 1);
    }
    return withoutPrefix;
  }

  // Property nodes
  if (nodeId.startsWith('prop_')) {
    const parts = nodeId.replace('prop_', '').split('_');
    const propertyName = parts[parts.length - 1];
    return propertyName;
  }

  // Schema nodes
  if (nodeId.startsWith('schema_')) {
    return nodeId.replace('schema_', '');
  }

  return nodeId;
}
